import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FolderArchive,
  Upload,
  Search,
  FileText,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/admin';
import { Button, Modal, Spinner } from '@/components/common';
import { DataTable } from '@/components/ui/data-table';
import { documents as docsApi } from '@/utils/api';
import { toast } from 'sonner';
import { useDocuments, useDocumentStats } from '@/hooks/useDocuments';
import { useAuth } from '@/context/AuthContext';
import { useDocumentJobStates, type JobStateMap } from '@/hooks/useDocumentJobStates';
import DocumentUploadModal from '@/components/admin/DocumentUploadModal';
import DocumentJobBadge from '@/components/admin/DocumentJobBadge';
import DocumentDetailModal from './dokumente/DocumentDetailModal';

// Kategorie-Konfiguration
// F9: Neue Reihenfolge + Kategorie "Prüfblätter"
const CATEGORY_CONFIG = {
  eingangsrechnung:             { label: 'Eingangsrechnung',         color: 'bg-blue-100 text-blue-700' },
  mahnung:                      { label: 'Mahnung',                  color: 'bg-red-100 text-red-700' },
  ausgangsrechnung:             { label: 'Ausgangsrechnung',         color: 'bg-green-100 text-green-700' },
  pruefblaetter:                { label: 'Prüfblätter',              color: 'bg-teal-100 text-teal-700' },
  kontoauszuege:                { label: 'Kontoauszüge',             color: 'bg-indigo-100 text-indigo-700' },
  anwalt_behoerden:             { label: 'Anwalt/Behörden',          color: 'bg-purple-100 text-purple-700' },
  versicherung_krankenkasse_bg: { label: 'Versicherung/KK/BG',      color: 'bg-cyan-100 text-cyan-700' },
  steuern_steuerbüro:          { label: 'Steuern/Steuerbüro',      color: 'bg-amber-100 text-amber-700' },
  vertraege:                    { label: 'Verträge',                 color: 'bg-emerald-100 text-emerald-700' },
  sonstiges:                    { label: 'Sonstiges',                color: 'bg-gray-100 text-gray-700' },
};

const PAYMENT_METHODS = ['Überweisung', 'Bar', 'Karte', 'PayPal'];

// Buchhaltungs-Status (seit Juli 2026 kein Automatismus mehr — das Backoffice
// entscheidet, welches Dokument als Rechnung in die Buchhaltung wandert).
// 'transferred' leitet das Backend aus documents.invoice_id ab.
//
// Wird als EIGENE Spalte gerendert, nicht als Badge neben dem Titel: die Titel
// sind lang und werden abgeschnitten, ein angehängter Badge ist dort praktisch
// unsichtbar. „Ist die Rechnung schon in der Buchhaltung?" muss man in einer
// Spalte herunterlesen können.
const BOOKING_CONFIG = {
  pending: {
    label: 'Offen',
    hint: 'Als Rechnung erkannt, noch nicht in die Buchhaltung übernommen',
    color: 'bg-orange-100 text-orange-800 ring-1 ring-orange-300',
  },
  transferred: {
    label: 'Übertragen',
    hint: 'In der Buchhaltung — klicken zum Öffnen',
    color: 'bg-green-100 text-green-700',
  },
  ignored: {
    label: 'ausgeschlossen',
    hint: 'Bewusst nicht für die Buchhaltung vorgesehen',
    color: 'text-gray-400',
  },
};

const STATUS_CONFIG = {
  pending:    { label: 'Ausstehend',   color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  processing: { label: 'Wird analysiert', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  completed:  { label: 'Fertig',       color: 'bg-green-100 text-green-700', icon: CheckCircle },
  failed:     { label: 'Fehler',       color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Column definitions factory
// jobStates: Pipeline v3 Stage-Status pro Dokument-ID (vom useDocumentJobStates Hook).
// onRetry: wird vom Badge bei failed_ocr / failed_title aufgerufen.
function dokumenteColumns(
  openDetail: (id: number) => void,
  jobStates: JobStateMap,
  onRetry: (docId: number) => void,
  onTransfer: (docId: number) => void,
  onOpenInvoice: (invoiceId: number) => void,
  // IDs, deren Übernahme gerade läuft — der Knopf quittiert den Klick sofort,
  // statt bis zum Serverergebnis unverändert dazustehen.
  transferring: Set<number>,
): ColumnDef<any, any>[] {
  return [
    {
      id: 'select',
      size: 52,
      minSize: 52,
      enableResizing: false,
      enableHiding: false,
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label="Alle auswählen"
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          checked={table.getIsAllPageRowsSelected()}
          ref={(el) => { if (el) el.indeterminate = table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected(); }}
          onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          aria-label="Dokument auswählen"
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-30"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'title',
      header: 'Titel',
      meta: { label: 'Titel' },
      size: 420,
      cell: ({ row }) => {
        const doc = row.original;
        return (
          <div className="flex items-center gap-2 min-w-0">
            {doc.is_done
              ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              : <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
            }
            <span className={`font-medium truncate ${doc.is_done ? 'text-gray-400' : 'text-gray-900'}`}>
              {doc.title || doc.original_file_name}
            </span>
          </div>
        );
      },
    },
    {
      id: 'booking',
      // normal-case: die sortierbaren Kopfzeilen stecken in einem <button>, der
      // das `uppercase` der Kopfzelle nicht erbt — ohne das stünde hier als
      // einziger Kopf "BUCHHALTUNG" in Großbuchstaben.
      header: () => <span className="normal-case">Buchhaltung</span>,
      meta: { label: 'Buchhaltung' },
      size: 170,
      cell: ({ row }) => {
        const doc = row.original;
        const state = doc.booking_status as keyof typeof BOOKING_CONFIG | null;
        // Kein Rechnungsdokument → Spalte bleibt leer, kein visuelles Rauschen
        if (!state) return <span className="text-gray-300">–</span>;
        const cfg = BOOKING_CONFIG[state];

        if (state === 'transferred') {
          return (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenInvoice(doc.invoice_id); }}
              title={cfg.hint}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium hover:brightness-95 transition ${cfg.color}`}
            >
              <CheckCircle className="w-3.5 h-3.5" />
              {cfg.label}
            </button>
          );
        }

        if (state === 'pending') {
          const busy = transferring.has(doc.id);
          return (
            <button
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); onTransfer(doc.id); }}
              title={busy ? 'Wird übernommen…' : 'Jetzt in die Buchhaltung übernehmen'}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition ${cfg.color} ${
                busy ? 'opacity-60 cursor-wait' : 'hover:brightness-95'
              }`}
            >
              {busy
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <AlertTriangle className="w-3.5 h-3.5" />}
              {busy ? 'Übernehme…' : cfg.label}
            </button>
          );
        }

        // 'ignored' bewusst leise — der stillgestellte Altbestand darf die Spalte
        // nicht zukleistern
        return <span className={`text-xs ${cfg.color}`} title={cfg.hint}>{cfg.label}</span>;
      },
    },
    {
      accessorKey: 'category',
      header: 'Kategorie',
      meta: { label: 'Kategorie' },
      size: 170,
      cell: ({ getValue }) => {
        const cat = getValue() as string;
        const catCfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.sonstiges;
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${catCfg.color}`}>
            {catCfg.label}
          </span>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Datum',
      meta: { label: 'Datum' },
      size: 110,
      cell: ({ getValue }) => (
        <span className="text-gray-600 hidden md:inline">{formatDate(getValue() as string)}</span>
      ),
    },
    {
      accessorKey: 'analysis_status',
      header: 'Status',
      meta: { label: 'Status (Verarbeitung)' },
      size: 190,
      cell: ({ row }) => {
        const doc = row.original;
        const js = jobStates[doc.id];
        // Pipeline v3: Wenn Job-State da ist, zeige ihn (auch 'done' nicht
        // unterdruecken, damit der User sieht dass die Pipeline durch ist).
        // Fallback auf legacy analysis_status fuer alte Dokumente ohne Jobs.
        return (
          <DocumentJobBadge
            jobState={js}
            legacyAnalysisStatus={doc.analysis_status}
            onRetry={() => onRetry(doc.id)}
          />
        );
      },
    },
  ];
}

const Dokumentenablage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // '' = alle, 'pending' | 'transferred' | 'ignored'
  const [bookingFilter, setBookingFilter] = useState('');
  // IDs mit laufender Buchhaltungs-Übernahme (nur für die Knopf-Rückmeldung)
  const [transferring, setTransferring] = useState<Set<number>>(new Set());

  // Mehrfachauswahl fuer die Sammel-Uebernahme in die Buchhaltung
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // Debounce search: update query only after 400ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Upload
  const [uploadModal, setUploadModal] = useState(false);

  // Detail
  const [detailDoc, setDetailDoc] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editCategory, setEditCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [doneFilter, setDoneFilter] = useState(''); // '' = alle, 'open' = nicht erledigt, 'done' = erledigt

  // Model Comparison
  const [compareModal, setCompareModal] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareResult, setCompareResult] = useState(null);

  // Build query params for documents list (uses debounced search to avoid refetch on every keystroke)
  const queryParams: Record<string, any> = {};
  if (categoryFilter) queryParams.category = categoryFilter;
  if (debouncedSearch) queryParams.search = debouncedSearch;
  if (statusFilter) queryParams.status = statusFilter;
  if (bookingFilter) queryParams.booking = bookingFilter;

  // Auto-Polling: 3s when any doc is processing, else 5s
  const { data: docsList = [], isLoading: loading } = useDocuments(
    Object.keys(queryParams).length > 0 ? queryParams : undefined,
  );
  const { data: stats } = useDocumentStats();

  const hasPending = docsList.some(
    (d: any) => d.analysis_status === 'pending' || d.analysis_status === 'processing',
  );

  // Pipeline v3: Job-Stage-Status pro Dokument batchen (Polling alle 15s wenn pending_*)
  const docIds = (docsList as any[]).map((d) => d.id).filter((x: any) => Number.isFinite(x));
  const { jobStates } = useDocumentJobStates(docIds);

  // Polling: refetch when there are pending docs (3s), otherwise slower (30s)
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const ms = hasPending ? 3000 : 30000;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    }, ms);
    return () => clearInterval(interval);
  }, [hasPending, queryClient]);

  // Mutations
  const invalidateDocs = () => queryClient.invalidateQueries({ queryKey: ['documents'] });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => docsApi.delete(id),
    onSuccess: () => {
      toast.success('Dokument gelöscht');
      setDetailDoc(null);
      invalidateDocs();
    },
    onError: () => toast.error('Fehler beim Löschen'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, any> }) =>
      docsApi.update(id, data),
    onSuccess: (_res, { data }) => {
      invalidateDocs();
      // Die Mitarbeiter-Markierung schreibt das Backend bei uebernommenen
      // Dokumenten bis auf die Rechnung durch (#124) — sonst haengt das MT-Badge
      // in der Buchhaltung am alten Cache-Stand.
      if (data.is_employee_invoice !== undefined || data.employee_name !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
      }
    },
    onError: () => toast.error('Fehler beim Aktualisieren'),
  });

  // Manuelle Uebernahme in die Buchhaltung (einzeln)
  const toInvoiceMutation = useMutation({
    mutationFn: (id: number) => docsApi.toInvoice(id),
    /*
     * Optimistisch: das Badge springt SOFORT auf "In Buchhaltung".
     * Vorher wartete die Zeile auf das Neuladen der Liste — die Kollegen sahen
     * nach dem Klick sekundenlang nichts und haben F5 gedrückt. Bei Fehlschlag
     * wird der vorherige Stand aus dem Cache zurückgerollt.
     */
    onMutate: async (id: number) => {
      setTransferring((prev) => new Set(prev).add(id));
      await queryClient.cancelQueries({ queryKey: ['documents'] });
      const snapshot = queryClient.getQueriesData({ queryKey: ['documents'] });
      queryClient.setQueriesData({ queryKey: ['documents'] }, (old: any) =>
        // Der Prefix trifft auch ['documents','stats'] — das ist ein Objekt, kein Array.
        Array.isArray(old)
          ? old.map((d: any) => (d.id === id ? { ...d, booking_status: 'transferred' } : d))
          : old,
      );
      return { snapshot };
    },
    onError: (err: any, _id, ctx: any) => {
      ctx?.snapshot?.forEach(([key, data]: [any, any]) => queryClient.setQueryData(key, data));
      toast.error(err?.response?.data?.error || 'Übernahme fehlgeschlagen');
    },
    onSuccess: async (res, id) => {
      if (res.data?.already) {
        toast.info('Dokument war bereits in der Buchhaltung');
      } else {
        toast.success('In die Buchhaltung übernommen');
      }
      invalidateDocs();
      queryClient.invalidateQueries({ queryKey: ['documents', 'stats'] });
      // Nur nachladen, wenn das Detail GENAU dieses Dokuments offen ist — sonst
      // würde ein Klick auf den Status in der Tabelle das Modal aufreißen.
      setDetailDoc((prev: any) => {
        if (!prev || prev.id !== id) return prev;
        docsApi.getById(id).then((fresh) => setDetailDoc(fresh.data)).catch(() => {});
        return prev;
      });
    },
    onSettled: (_res, _err, id) => {
      setTransferring((prev) => { const next = new Set(prev); next.delete(id as number); return next; });
    },
  });

  // Manuelle Uebernahme in die Buchhaltung (Mehrfachauswahl)
  const toInvoiceBulkMutation = useMutation({
    mutationFn: (ids: number[]) => docsApi.toInvoiceBulk(ids),
    // Gleiche Sofortreaktion wie bei der Einzel-Übernahme
    onMutate: async (ids: number[]) => {
      setTransferring((prev) => { const next = new Set(prev); ids.forEach((i) => next.add(i)); return next; });
      await queryClient.cancelQueries({ queryKey: ['documents'] });
      const snapshot = queryClient.getQueriesData({ queryKey: ['documents'] });
      const set = new Set(ids);
      queryClient.setQueriesData({ queryKey: ['documents'] }, (old: any) =>
        Array.isArray(old)
          ? old.map((d: any) => (set.has(d.id) ? { ...d, booking_status: 'transferred' } : d))
          : old,
      );
      return { snapshot };
    },
    onError: (err: any, _ids, ctx: any) => {
      ctx?.snapshot?.forEach(([key, data]: [any, any]) => queryClient.setQueryData(key, data));
      toast.error(err?.response?.data?.error || 'Sammel-Übernahme fehlgeschlagen');
    },
    onSuccess: (res) => {
      const { created = 0, skipped = 0, failed = 0, results = [] } = res.data || {};
      if (created > 0) toast.success(`${created} Rechnung(en) in die Buchhaltung übernommen`);
      if (skipped > 0) toast.info(`${skipped} waren bereits übernommen`);
      if (failed > 0) {
        const reason = results.find((r: any) => !r.ok)?.error;
        toast.error(`${failed} fehlgeschlagen${reason ? `: ${reason}` : ''}`);
      }
      setRowSelection({});
      invalidateDocs();
      queryClient.invalidateQueries({ queryKey: ['documents', 'stats'] });
    },
    onSettled: (_res, _err, ids) => {
      setTransferring((prev) => {
        const next = new Set(prev);
        (ids as number[]).forEach((i) => next.delete(i));
        return next;
      });
    },
  });

  const reanalyzeMutation = useMutation({
    mutationFn: (id: number) =>
      fetch(`/api/documents/${id}/reanalyze`, { method: 'POST', credentials: 'include' }),
    onSuccess: (_res, id) => {
      setDetailDoc((prev: any) => prev ? { ...prev, analysis_status: 'processing', analysis_error: null } : prev);
      toast.info('Analyse wird erneut gestartet...');
      invalidateDocs();
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const res = await docsApi.getById(id);
          if (res.data.analysis_status !== 'processing') {
            clearInterval(poll);
            setDetailDoc(res.data);
            invalidateDocs();
            if (res.data.analysis_status === 'completed') {
              toast.success('Analyse erfolgreich!');
            }
          }
        } catch { clearInterval(poll); }
      }, 3000);
      setTimeout(() => clearInterval(poll), 300000);
    },
    onError: () => toast.error('Fehler beim erneuten Starten'),
  });

  // Open document from URL parameter (e.g. from Chatbot link)
  useEffect(() => {
    const openDocId = searchParams.get('openDoc');
    if (openDocId) {
      openDetail(parseInt(openDocId));
      setSearchParams({}, { replace: true }); // Clean URL
    }
  }, [searchParams]);

  // Open detail
  const openDetail = async (docId) => {
    setDetailLoading(true);
    setDetailDoc(null);
    try {
      const res = await docsApi.getById(docId);
      setDetailDoc(res.data);
      setEditCategory(res.data.category || 'sonstiges');
    } catch (err) {
      toast.error('Fehler beim Laden des Dokuments');
    } finally {
      setDetailLoading(false);
    }
  };

  // Delete document
  const handleDelete = (id: number) => {
    // Diese Richtung löscht bewusst weiter kaskadierend: das Dokument ist das
    // Original, eine Buchung ohne Beleg wäre wertlos. Aber das darf nicht
    // stillschweigend passieren — umgekehrt (Löschen in der Buchhaltung) bleibt
    // das Dokument seit 27.07.2026 erhalten.
    const doc = (docsList as any[]).find((d) => d.id === id) || detailDoc;
    const warnung = doc?.invoice_id
      ? '\n\nACHTUNG: Zu diesem Dokument gehört eine Rechnung in der Buchhaltung. Sie wird mitgelöscht.'
      + '\n\nSoll nur die Buchung entfernt werden, das Dokument aber bleiben? Dann in der Buchhaltung'
      + ' „Aus Buchhaltung entfernen" benutzen.'
      : '';
    if (!window.confirm(`Dokument wirklich löschen?${warnung}`)) return;
    deleteMutation.mutate(id);
  };

  // Update category
  const handleCategoryChange = (id: number, newCategory: string) => {
    const invoiceCategories = ['eingangsrechnung', 'ausgangsrechnung', 'mahnung'];
    const oldCategory = detailDoc?.category;
    // Seit Juli 2026 entsteht hier KEINE Rechnung mehr — das Dokument wird nur
    // als "wartet auf Uebernahme" markiert.
    const willBecomeBookable =
      !invoiceCategories.includes(oldCategory) &&
      invoiceCategories.includes(newCategory) &&
      !detailDoc?.invoice_id;

    setEditCategory(newCategory);
    if (detailDoc) setDetailDoc({ ...detailDoc, category: newCategory });

    updateMutation.mutate(
      { id, data: { category: newCategory } },
      {
        onSuccess: (res: any) => {
          if (res?.data) setDetailDoc(res.data);
          if (willBecomeBookable) {
            toast.success('Kategorie geändert — zur Übernahme in die Buchhaltung vorgemerkt');
          }
        },
        onError: () => {
          // Revert optimistic update
          setEditCategory(detailDoc?.category || 'sonstiges');
          if (detailDoc) setDetailDoc({ ...detailDoc, category: oldCategory });
        },
      },
    );
  };

  // Render stats cards
  const renderStats = () => {
    if (!stats) return null;
    const categories = Object.keys(CATEGORY_CONFIG);
    const pendingBooking = stats.pendingBooking || 0;
    return (
      <>
      {pendingBooking > 0 && (
        <button
          onClick={() => { setBookingFilter(bookingFilter === 'pending' ? '' : 'pending'); setRowSelection({}); }}
          className={`w-full mb-3 flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all ${
            bookingFilter === 'pending'
              ? 'border-orange-500 bg-orange-100 shadow-md'
              : 'border-orange-200 bg-orange-50 hover:border-orange-400'
          }`}
        >
          <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
          <span className="text-sm text-orange-900">
            <span className="font-semibold">{pendingBooking}</span>{' '}
            {pendingBooking === 1 ? 'Rechnung wartet' : 'Rechnungen warten'} auf die Übernahme in die Buchhaltung
          </span>
          <span className="ml-auto text-xs font-medium text-orange-700">
            {bookingFilter === 'pending' ? 'Filter aufheben' : 'Anzeigen'}
          </span>
        </button>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {categories.map(cat => {
          const cfg = CATEGORY_CONFIG[cat];
          const count = stats.byCategory?.[cat] || 0;
          return (
            <button
              key={cat}
              onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
              className={`p-3 rounded-lg text-center transition-all border-2 ${
                categoryFilter === cat
                  ? 'border-primary-500 shadow-md'
                  : 'border-transparent hover:border-gray-200'
              } ${cfg.color}`}
            >
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-xs font-medium truncate">{cfg.label}</div>
            </button>
          );
        })}
      </div>
      </>
    );
  };

  // Parse tags
  const parseTags = (tagsStr) => {
    if (!tagsStr) return [];
    try { return JSON.parse(tagsStr); }
    catch { return []; }
  };

  // Spalten memoisieren: beim Ziehen der Spaltenbreite rendert die Tabelle pro
  // Mausbewegung. Wuerden die Spalten-Definitionen dabei jedes Mal neu erzeugt,
  // verliert TanStack die Spalten-Identitaet und der Drag reisst ab.
  const tableColumns = useMemo(
    () => dokumenteColumns(
      openDetail,
      jobStates,
      (id) => reanalyzeMutation.mutate(id),
      (id) => toInvoiceMutation.mutate(id),
      (invoiceId) => navigate(`/admin/buchhaltung?invoice=${invoiceId}`),
      transferring,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobStates, transferring],
  );

  // Der Erledigt-Filter wird clientseitig angewandt (kein Backend-Filter dafuer)
  const visibleDocs = (docsList as any[]).filter((doc) => {
    if (doneFilter === 'open') return !doc.is_done;
    if (doneFilter === 'done') return !!doc.is_done;
    return true;
  });

  const selectedIds = Object.keys(rowSelection)
    .filter((k) => rowSelection[k])
    .map(Number);
  // Nur uebernehmen, was gerade sichtbar UND wirklich offen ist — eine Auswahl
  // kann stehenbleiben, nachdem ein Dokument bereits gebucht wurde.
  const selectedPendingIds = visibleDocs
    .filter((d: any) => selectedIds.includes(d.id) && d.booking_status === 'pending')
    .map((d: any) => d.id);

  if (loading && docsList.length === 0) return <Spinner.Page />;

  return (
    <div>
      <PageHeader
        title="Dokumentenablage"
        subtitle={`${stats?.total || 0} Dokumente`}
        actions={
          <Button onClick={() => { setUploadModal(true); }}>
            <Upload className="w-4 h-4 mr-2" />
            Hochladen
          </Button>
        }
      />

      {/* Stats */}
      {renderStats()}

      {/* Filters */}
      <div className="rounded-xl shadow-sm border border-gray-100 bg-white mb-4">
        <div className="flex flex-wrap gap-4 px-4 py-3">
          <div className="relative min-w-[200px] max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suche..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm">
            <option value="">Alle Kategorien</option>
            {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm">
            <option value="">Alle Status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select value={doneFilter} onChange={(e) => setDoneFilter(e.target.value)} className="flex h-9 rounded-md border border-input bg-white px-3 py-1 text-sm">
            <option value="">Alle</option>
            <option value="open">Offen</option>
            <option value="done">Erledigt</option>
          </select>
          <select value={bookingFilter} onChange={(e) => { setBookingFilter(e.target.value); setRowSelection({}); }} className="flex h-9 w-52 rounded-md border border-input bg-white px-3 py-1 text-sm">
            <option value="">Buchhaltung: alle</option>
            <option value="pending">Noch nicht übernommen</option>
            <option value="transferred">In der Buchhaltung</option>
            <option value="ignored">Nicht für Buchhaltung</option>
          </select>
          {(categoryFilter || searchTerm || statusFilter || doneFilter || bookingFilter) && (
            <button onClick={() => { setCategoryFilter(''); setSearchTerm(''); setDebouncedSearch(''); setStatusFilter(''); setDoneFilter(''); setBookingFilter(''); setRowSelection({}); }} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <X className="w-3 h-3" /> Zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Documents Table */}
      {docsList.length === 0 && !loading ? (
        <div className="bg-white rounded-lg shadow px-4 py-12 text-center text-gray-500">
          <FolderArchive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Keine Dokumente gefunden</p>
          <button
            onClick={() => { setUploadModal(true); }}
            className="text-primary-600 hover:text-primary-700 text-sm mt-2"
          >
            Erstes Dokument hochladen
          </button>
        </div>
      ) : (
        <>
          {selectedPendingIds.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg border border-primary-200 bg-primary-50">
              <span className="text-sm text-gray-700">
                <span className="font-semibold">{selectedIds.length}</span> ausgewählt
                {selectedPendingIds.length !== selectedIds.length && (
                  <span className="text-gray-500">
                    {' '}— davon {selectedPendingIds.length} noch nicht in der Buchhaltung
                  </span>
                )}
              </span>
              <Button
                onClick={() => toInvoiceBulkMutation.mutate(selectedPendingIds)}
                disabled={toInvoiceBulkMutation.isPending}
              >
                {toInvoiceBulkMutation.isPending
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <ExternalLink className="w-4 h-4 mr-2" />}
                {selectedPendingIds.length} in die Buchhaltung übernehmen
              </Button>
              <button
                onClick={() => setRowSelection({})}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Auswahl aufheben
              </button>
            </div>
          )}
          <DataTable
            columns={tableColumns}
            data={visibleDocs}
            onRowClick={(doc) => openDetail(doc.id)}
            getRowClassName={(doc: any) =>
              doc.booking_status === 'pending' ? 'bg-orange-50/60 hover:bg-orange-50' : undefined
            }
            enableColumnResizing
            enableColumnVisibility
            // pro Benutzer: Kollegen an einem gemeinsamen Rechner sollen sich
            // ihre Spaltenauswahl nicht gegenseitig ueberschreiben
            storageKey={`dokumentenablage:${user?.id ?? 'anon'}`}
            pageSize={25}
            emptyText="Keine Dokumente gefunden"
            className="pb-24"
            paginationClassName="pr-24"
            enableRowSelection={(row) => (row.original as any)?.booking_status === 'pending'}
            getRowId={(row: any) => String(row.id)}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
          />
        </>
      )}

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={uploadModal}
        onClose={() => setUploadModal(false)}
        onUploadsComplete={invalidateDocs}
      />

      {/* Detail-Modal — Fullscreen-Split, baugleich zum Rechnungs-Modal der
          Buchhaltung (Kundenwunsch „einheitlicher Look"). Inhalt in
          ./dokumente/DocumentDetailModal. */}
      <DocumentDetailModal
        doc={detailDoc}
        loading={detailLoading}
        categories={CATEGORY_CONFIG}
        paymentMethods={PAYMENT_METHODS}
        statusConfig={STATUS_CONFIG}
        transferPending={!!detailDoc && transferring.has(detailDoc.id)}
        onClose={() => setDetailDoc(null)}
        handlers={{
          onSave: async (field, value) => {
            if (field === 'category') return handleCategoryChange(detailDoc.id, value as string);
            setDetailDoc((prev: any) => (prev ? { ...prev, [field]: value } : prev));
            await updateMutation.mutateAsync({ id: detailDoc.id, data: { [field]: value } });
          },
          onToggleDone: (done) => {
            const val = done ? 1 : 0;
            setDetailDoc((prev: any) => (prev ? { ...prev, is_done: val } : prev));
            updateMutation.mutate({ id: detailDoc.id, data: { is_done: val } });
          },
          onSaveTags: (tags) => {
            const asJson = JSON.stringify(tags);
            setDetailDoc((prev: any) => (prev ? { ...prev, tags: asJson } : prev));
            updateMutation.mutate({ id: detailDoc.id, data: { tags: asJson } });
          },
          onTransfer: () => toInvoiceMutation.mutate(detailDoc.id),
          onSetBookingState: (state) => updateMutation.mutate(
            { id: detailDoc.id, data: { booking_state: state } },
            {
              onSuccess: (res: any) => {
                setDetailDoc(res.data);
                toast[state === 'ignored' ? 'success' : 'info'](
                  state === 'ignored'
                    ? 'Als „nicht für Buchhaltung" markiert'
                    : 'Wieder zur Übernahme vorgemerkt',
                );
              },
            },
          ),
          onOpenInvoice: (invoiceId) => navigate(`/admin/buchhaltung?invoice=${invoiceId}`),
          onReanalyze: () => reanalyzeMutation.mutate(detailDoc.id),
          onDelete: () => handleDelete(detailDoc.id),
        }}
      />

      {/* Compare Modal — Opus vs Sonnet */}
      <Modal
        isOpen={compareModal}
        onClose={() => { setCompareModal(false); setCompareResult(null); }}
        title={compareResult ? `Vergleich: ${compareResult.document?.title || 'Dokument'}` : 'Modell-Vergleich läuft...'}
        size="full"
      >
        {compareLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
            <p className="text-gray-600">Beide Modelle analysieren das Dokument parallel...</p>
            <p className="text-xs text-gray-400 mt-1">Opus 4.5 + Sonnet 4.5 — kann 30-60s dauern</p>
          </div>
        ) : compareResult ? (
          <div className="max-h-[80vh] overflow-auto">
            {/* Stats bar */}
            <div className="flex gap-4 mb-4 p-3 bg-gray-50 rounded-lg text-xs">
              {[compareResult.opus, compareResult.sonnet].map(r => (
                <div key={r.model} className="flex-1 flex items-center gap-3">
                  <span className={`font-bold ${r.model === 'opus' ? 'text-purple-700' : 'text-blue-700'}`}>{r.modelLabel}</span>
                  <span className="text-gray-500">{(r.durationMs / 1000).toFixed(1)}s</span>
                  <span className="text-gray-500">{r.usage.inputTokens + r.usage.outputTokens} Tokens</span>
                  <span className="text-gray-500">${r.usage.cost.toFixed(4)}</span>
                </div>
              ))}
            </div>

            {/* Side by side */}
            <div className="grid grid-cols-2 gap-4">
              {[compareResult.opus, compareResult.sonnet].map(r => {
                const res = r.result || {};
                const cat = CATEGORY_CONFIG[res.category] || { label: res.category, color: 'bg-gray-100 text-gray-700' };
                return (
                  <div key={r.model} className={`border rounded-lg overflow-hidden ${r.model === 'opus' ? 'border-purple-200' : 'border-blue-200'}`}>
                    <div className={`px-4 py-2 text-sm font-bold ${r.model === 'opus' ? 'bg-purple-50 text-purple-800' : 'bg-blue-50 text-blue-800'}`}>
                      {r.modelLabel}
                    </div>
                    <div className="p-4 space-y-3 text-sm">
                      <div>
                        <span className="text-gray-500 text-xs block">Kategorie</span>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cat.color}`}>{cat.label}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs block">Titel</span>
                        <span className="font-medium">{res.title || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs block">Zusammenfassung</span>
                        <p className="text-gray-800 bg-gray-50 rounded p-2 text-xs">{res.summary || '-'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs block">Schlagworte</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(res.tags || []).map((t, i) => (
                            <span key={i} className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-xs">{t}</span>
                          ))}
                        </div>
                      </div>
                      {res.invoice_data && (
                        <div>
                          <span className="text-gray-500 text-xs block">Rechnungsdaten</span>
                          <div className="bg-gray-50 rounded p-2 text-xs space-y-1">
                            {res.invoice_data.invoice_number && <div>Nr: <strong>{res.invoice_data.invoice_number}</strong></div>}
                            {res.invoice_data.supplier_name && <div>Lieferant: {res.invoice_data.supplier_name}</div>}
                            {res.invoice_data.gross_amount != null && <div>Brutto: <strong>{Number(res.invoice_data.gross_amount).toFixed(2)} €</strong></div>}
                            {res.invoice_data.net_amount != null && <div>Netto: {Number(res.invoice_data.net_amount).toFixed(2)} €</div>}
                            {res.invoice_data.due_date && <div>Fällig: {res.invoice_data.due_date}</div>}
                            {res.invoice_data.supplier_iban && <div>IBAN: <span className="font-mono">{res.invoice_data.supplier_iban}</span></div>}
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500 text-xs block">Extrahierter Text (Auszug)</span>
                        <p className="text-gray-600 bg-gray-50 rounded p-2 text-xs max-h-40 overflow-auto whitespace-pre-wrap">
                          {(res.extracted_text || '-').substring(0, 1000)}
                          {(res.extracted_text || '').length > 1000 ? '...' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default Dokumentenablage;
